/**
 * Users API client
 */

import { apiClient } from './client';
import type { User, UserRole } from '../../types/user';

export interface UserCreateData {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

export interface UserUpdateData {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  isActive?: boolean;
}

export interface UserLimitStatus {
  activeUsers: number;
  maxUsers: number;
  percentage: number;
  hasWarning: boolean;
  isAtLimit: boolean;
  availableSlots: number;
}

export const usersApi = {
  /**
   * Create a new user
   */
  async create(companyId: string, data: UserCreateData): Promise<User> {
    // Transform camelCase to snake_case for API
    const payload = {
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      role: data.role,
    };
    const response = await apiClient.post<User>(`/api/v1/users?company_id=${companyId}`, payload);
    return response.data;
  },

  /**
   * List users from a company
   */
  async list(
    companyId: string,
    options?: {
      role?: UserRole;
      activeOnly?: boolean;
    }
  ): Promise<User[]> {
    const params = new URLSearchParams({ company_id: companyId });
    
    if (options?.role) {
      params.append('role', options.role);
    }
    
    if (options?.activeOnly) {
      params.append('active_only', 'true');
    }
    
    const response = await apiClient.get<User[]>(`/api/v1/users?${params.toString()}`);
    return response.data;
  },

  /**
   * Get user by ID
   */
  async getById(userId: string, companyId: string): Promise<User> {
    const response = await apiClient.get<User>(`/api/v1/users/${userId}?company_id=${companyId}`);
    return response.data;
  },

  /**
   * Update user
   */
  async update(userId: string, companyId: string, data: UserUpdateData): Promise<User> {
    // Transform camelCase to snake_case for API
    const payload: any = {};
    if (data.email !== undefined) payload.email = data.email;
    if (data.firstName !== undefined) payload.first_name = data.firstName;
    if (data.lastName !== undefined) payload.last_name = data.lastName;
    if (data.role !== undefined) payload.role = data.role;
    if (data.isActive !== undefined) payload.is_active = data.isActive;
    
    const response = await apiClient.put<User>(`/api/v1/users/${userId}?company_id=${companyId}`, payload);
    return response.data;
  },

  /**
   * Deactivate user
   */
  async deactivate(userId: string, companyId: string): Promise<void> {
    await apiClient.patch(`/api/v1/users/${userId}/deactivate?company_id=${companyId}`);
  },

  /**
   * Activate user
   */
  async activate(userId: string, companyId: string): Promise<User> {
    const response = await apiClient.patch<User>(`/api/v1/users/${userId}/activate?company_id=${companyId}`);
    return response.data;
  },

  /**
   * Get user limit status
   */
  async getLimitStatus(companyId: string): Promise<UserLimitStatus> {
    const response = await apiClient.get<UserLimitStatus>(`/api/v1/users/limit/status?company_id=${companyId}`);
    return response.data;
  },
};
